import type { CreateGroupState, RestrictionChange } from './createGroupTypes';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { SliceActions } from '../../reduxHelper';

export const initialState: CreateGroupState = {
    showCreateGroupDialog: false,
    rolesToAdd: [],
    restrictionsToAdd: [],
    newGroupName: '',
    newGroupDescription: '',
    groupCreationInProgress: false,
};

const compareRelevantFields = (restriction: RestrictionChange, restrictionToBeRemoved: RestrictionChange) =>
    restriction.id !== restrictionToBeRemoved.id || restriction.parentId !== restrictionToBeRemoved.parentId;

const createGroupSlice = createSlice({
    name: 'userSidebarSlice',
    initialState,
    reducers: {
        addRole(state, action: PayloadAction<string>) {
            state.rolesToAdd = [...state.rolesToAdd, action.payload];
        },
        removeRole(state, action: PayloadAction<string>) {
            state.rolesToAdd = state.rolesToAdd.filter(roleId => roleId !== action.payload);
            state.restrictionsToAdd = state.restrictionsToAdd.filter(
                restriction => restriction.parentId !== action.payload
            );
        },
        addRestriction(state, action: PayloadAction<RestrictionChange>) {
            const exists = state.restrictionsToAdd.some(
                restriction => restriction.id === action.payload.id && restriction.parentId === action.payload.parentId
            );
            if (!exists) {
                state.restrictionsToAdd.push(action.payload);
            }
        },
        setRestrictionBranch(state, action: PayloadAction<{ parentId: string; tagIds: string[]; selected: boolean }>) {
            const { parentId, tagIds, selected } = action.payload;
            const branchIds = new Set(tagIds);
            state.restrictionsToAdd = state.restrictionsToAdd.filter(
                restriction => restriction.parentId !== parentId || !branchIds.has(restriction.id)
            );
            if (selected) {
                const existing = new Set(
                    state.restrictionsToAdd
                        .filter(restriction => restriction.parentId === parentId)
                        .map(restriction => restriction.id)
                );
                for (const id of branchIds) {
                    if (!existing.has(id)) {
                        state.restrictionsToAdd.push({ id, parentId });
                    }
                }
            }
        },
        removeRestriction(state, action: PayloadAction<RestrictionChange>) {
            state.restrictionsToAdd = state.restrictionsToAdd.filter(restriction =>
                compareRelevantFields(restriction, action.payload)
            );
        },
        createGroupDescription(state, action: PayloadAction<string>) {
            state.newGroupDescription = action.payload;
        },
        createGroupName(state, action: PayloadAction<string>) {
            state.newGroupName = action.payload;
        },
        setGroupCreationInProgress(state, action: PayloadAction<boolean>) {
            state.groupCreationInProgress = action.payload;
        },
        resetCreateGroupState() {
            return initialState;
        },
    },
});

export type CreateGroupActionTypes = SliceActions<typeof createGroupSlice.actions>;
export default createGroupSlice;
